import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, AlertTriangle, XCircle, Undo2, Loader2, RotateCcw, TrendingUp, Flag } from "lucide-react";

const STATUS = {
  verified: { label: "Vérifié ✓", cls: "bg-green-100 text-green-800", Icon: ShieldCheck },
  published: { label: "Publié", cls: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  velo_pushed: { label: "Pont Velo — en attente de vérification", cls: "bg-blue-50 text-blue-700", Icon: CheckCircle2 },
  bridge_pushed: { label: "Pont Emergent — servi au chargement", cls: "bg-blue-50 text-blue-700", Icon: CheckCircle2 },
  manual_required: { label: "Manuel requis", cls: "bg-amber-50 text-amber-700", Icon: AlertTriangle },
  rolled_back: { label: "Annulée", cls: "bg-slate-100 text-slate-600", Icon: Undo2 },
  failed: { label: "Échec", cls: "bg-red-50 text-red-700", Icon: XCircle },
};

const RB_STATUS = {
  verified: { label: "Rollback vérifié ✓", cls: "bg-green-100 text-green-800", Icon: ShieldCheck },
  verification_failed: { label: "Rollback appliqué — vérification publique échouée", cls: "bg-red-50 text-red-700", Icon: XCircle },
  pending_verification: { label: "Rollback en attente de propagation", cls: "bg-amber-50 text-amber-700", Icon: AlertTriangle },
  applied: { label: "Rollback appliqué — vérification publique requise", cls: "bg-slate-100 text-slate-600", Icon: Undo2 },
};

const CAMP_STATUS = {
  verification_en_cours: { label: "Vérification en cours", cls: "bg-blue-50 text-blue-700" },
  terminee: { label: "Terminée", cls: "bg-green-100 text-green-800" },
  terminee_avec_erreurs: { label: "Terminée avec erreurs", cls: "bg-amber-50 text-amber-700" },
};

const EMPTY_FILTERS = { status: "", category: "", source: "", campaign_id: "", q: "" };

export function FixHistoryPanel({ siteId }) {
  const [history, setHistory] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const load = useCallback(async (f = filters) => {
    if (!siteId) return;
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const [{ data }, { data: cData }] = await Promise.all([
        api.get(`/seo-fix/history/${siteId}`, { params }),
        api.get(`/seo-fix/campaigns/${siteId}`),
      ]);
      setHistory(data.history || []);
      setCampaigns(cData.campaigns || []);
    } catch { /* noop */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => { setFilters(EMPTY_FILTERS); load(EMPTY_FILTERS); }, [load]);

  const applyFilters = (next) => { setFilters(next); load(next); };

  const verify = async (entry) => {
    setBusy(entry.id);
    try {
      const { data } = await api.post(`/seo-fix/verify/${entry.id}`);
      if (data.verified) {
        toast.success("Vérifié ✓ — la correction est visible publiquement");
        if (data.reaudit_started) toast.info("Re-audit automatique lancé — le nouveau score apparaîtra ici sous peu");
      } else toast.warning(data.note || "Pas encore visible publiquement — réessayez dans 2-3 minutes");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Vérification impossible"); }
    finally { setBusy(null); }
  };

  const verifyRollback = async (entry) => {
    setBusy(entry.id);
    try {
      const { data } = await api.post(`/seo-fix/verify-rollback/${entry.id}`);
      if (data.verified) toast.success("Rollback vérifié ✓ — l'ancienne valeur est de retour publiquement");
      else toast.warning(data.note);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Vérification du rollback impossible"); }
    finally { setBusy(null); }
  };

  const rollback = async (entry) => {
    if (!window.confirm("Annuler cette correction et restaurer la valeur précédente ?")) return;
    setBusy(entry.id);
    try {
      await api.post(`/seo-fix/rollback/${entry.id}`);
      toast.success("Rollback appliqué — vérifiez-le publiquement pour obtenir « Rollback vérifié ✓ »");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rollback impossible"); }
    finally { setBusy(null); }
  };

  const canRollback = (e) => ["published", "verified", "velo_pushed", "bridge_pushed"].includes(e.status)
    && (e.source === "wix_static" || (e.old_value || "").trim());
  const canVerify = (e) => ["published", "velo_pushed"].includes(e.status);
  const canVerifyRollback = (e) => e.status === "rolled_back"
    && e.rollback_verification?.status !== "verified"
    && ((e.old_value || "").trim() || e.source === "wix_static");

  const sel = "border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-700 bg-white";

  return (
    <div>
      {campaigns.length > 0 && (
        <div className="mb-4 border border-slate-200 bg-white rounded-md overflow-hidden" data-testid="campaigns-panel">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 overline flex items-center gap-2">
            <Flag className="w-3.5 h-3.5" /> Campagnes de correction ({campaigns.length})
          </div>
          <ul className="divide-y divide-slate-100">
            {campaigns.map((c, i) => {
              const st = CAMP_STATUS[c.status] || CAMP_STATUS.verification_en_cours;
              return (
                <li key={c.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`campaign-row-${i}`}>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-slate-900">{c.name}</span>
                    <span className={`ml-2 text-[11px] px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {new Date(c.created_at).toLocaleString("fr-FR")} · {c.created_by} ·
                      proposées {c.proposed} · conservées {c.kept} · appliquées {c.applied} · vérifiées {c.verified} · échecs {c.failed} · manuel {c.manual}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono" data-testid={`campaign-score-${i}`}>
                      Score {c.score_before ?? "?"} → {c.score_after ?? "…"}
                    </span>
                    <button onClick={() => applyFilters({ ...EMPTY_FILTERS, campaign_id: c.id })}
                      data-testid={`campaign-filter-${i}`} className="text-xs text-[#002FA7] underline">
                      Voir les corrections
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden" data-testid="fix-history-panel">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="overline">Historique des corrections ({history.length})</div>
          <button onClick={() => load()} data-testid="fix-history-refresh" className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Rafraîchir
          </button>
        </div>
        <div className="px-5 py-2.5 border-b border-slate-100 flex items-center gap-2 flex-wrap" data-testid="fix-history-filters">
          <select value={filters.status} onChange={(e) => applyFilters({ ...filters, status: e.target.value })} className={sel} data-testid="filter-status">
            <option value="">Statut : tous</option>
            <option value="verified">Vérifié ✓</option>
            <option value="published">Publié</option>
            <option value="velo_pushed">Pont Velo</option>
            <option value="bridge_pushed">Pont Emergent</option>
            <option value="manual_required">Manuel</option>
            <option value="rolled_back">Annulée</option>
            <option value="failed">Échec</option>
          </select>
          <select value={filters.category} onChange={(e) => applyFilters({ ...filters, category: e.target.value })} className={sel} data-testid="filter-category">
            <option value="">Catégorie : toutes</option>
            {["Titre SEO", "Meta description", "H1", "Canonical", "Open Graph", "Schema"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filters.source} onChange={(e) => applyFilters({ ...filters, source: e.target.value })} className={sel} data-testid="filter-source">
            <option value="">Source : toutes</option>
            <option value="wix">Wix Blog</option>
            <option value="wix_static">Wix statique (Velo)</option>
            <option value="github">GitHub</option>
            <option value="manual">Manuel</option>
          </select>
          {campaigns.length > 0 && (
            <select value={filters.campaign_id} onChange={(e) => applyFilters({ ...filters, campaign_id: e.target.value })} className={sel} data-testid="filter-campaign">
              <option value="">Campagne : toutes</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && applyFilters(filters)}
            placeholder="Recherche URL… (Entrée)" className={`${sel} w-44`} data-testid="filter-search" />
          <button onClick={() => applyFilters(EMPTY_FILTERS)} data-testid="filter-reset" className="text-xs text-slate-500 underline">
            Réinitialiser
          </button>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : !history.length ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="fix-history-empty">
            Aucune correction ne correspond à ces filtres.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {history.map((e, i) => {
              const st = STATUS[e.status] || STATUS.failed;
              const rb = e.status === "rolled_back" ? (RB_STATUS[e.rollback_verification?.status] || RB_STATUS.applied) : null;
              return (
                <li key={e.id} className="p-4" data-testid={`fix-history-row-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-slate-900 uppercase tracking-wider">{e.category}</span>
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ${st.cls}`}>
                          <st.Icon className="w-3 h-3" /> {st.label}
                        </span>
                        {rb && (
                          <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ${rb.cls}`} data-testid={`fix-history-rb-status-${i}`}>
                            <rb.Icon className="w-3 h-3" /> {rb.label}
                          </span>
                        )}
                        {e.method === "rollback" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">rollback</span>}
                        {e.reaudit && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700" data-testid={`fix-history-reaudit-${i}`}>
                            <TrendingUp className="w-3 h-3" />
                            Score {e.reaudit.previous_score ?? "?"} → {e.reaudit.new_score}
                          </span>
                        )}
                      </div>
                      <a href={e.page_url} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-[#002FA7] truncate block mt-0.5">{e.page_url}</a>
                      <div className="mt-1.5 text-xs">
                        <span className="text-slate-400 line-through">{e.old_value || "(vide)"}</span>
                        <span className="mx-1.5 text-slate-300">→</span>
                        <span className="text-slate-800">{e.new_value}</span>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {new Date(e.created_at).toLocaleString("fr-FR")} · {e.applied_by} · source {e.source}
                        {e.rollback_verification?.attempts > 0 && ` · vérif. rollback : ${e.rollback_verification.attempts} tentative(s)`}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                      {canVerify(e) && (
                        <button onClick={() => verify(e)} disabled={busy === e.id} data-testid={`fix-history-verify-${i}`}
                          className="text-xs font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-2 py-1 hover:bg-blue-50 inline-flex items-center gap-1">
                          {busy === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Vérifier
                        </button>
                      )}
                      {canVerifyRollback(e) && (
                        <button onClick={() => verifyRollback(e)} disabled={busy === e.id} data-testid={`fix-history-verify-rollback-${i}`}
                          className="text-xs font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-2 py-1 hover:bg-blue-50 inline-flex items-center gap-1">
                          {busy === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Vérifier le rollback
                        </button>
                      )}
                      {canRollback(e) && (
                        <button onClick={() => rollback(e)} disabled={busy === e.id} data-testid={`fix-history-rollback-${i}`}
                          className="text-xs font-medium text-slate-600 border border-slate-300 rounded-md px-2 py-1 hover:bg-slate-50 inline-flex items-center gap-1">
                          {busy === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Annuler
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
